import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import {
  fetchAiFinanceSettings,
  fetchAlertRules,
  fetchPublicSettings,
  patchAiFinanceSettings,
  patchPublicSettings,
  updateAlertRule,
} from '@/api/client'
import type { AiFinanceSettings } from '@/api/types'
import { ApiError } from '@/api/client'
import type { AlertRule } from '@/api/types'

import { Bell } from 'lucide-react'
import { AdminSection } from '@/features/admin/AdminSection'

export function AlertsAdminPage() {
  const qc = useQueryClient()
  const rulesQuery = useQuery({
    queryKey: ['config', 'alert-rules'],
    queryFn: fetchAlertRules,
  })
  const settingsQuery = useQuery({
    queryKey: ['config', 'settings'],
    queryFn: fetchPublicSettings,
  })
  const aiFinanceQuery = useQuery({
    queryKey: ['ai', 'settings', 'finance'],
    queryFn: fetchAiFinanceSettings,
  })

  const [error, setError] = useState<string | null>(null)
  const [rationalePct, setRationalePct] = useState('10')
  const [aiFinance, setAiFinance] = useState<AiFinanceSettings | null>(null)

  useEffect(() => {
    const raw = settingsQuery.data?.settings?.rationale_threshold_pct
    if (typeof raw === 'number') {
      setRationalePct(String(Math.round(raw * 1000) / 10))
    }
  }, [settingsQuery.data])

  useEffect(() => {
    if (aiFinanceQuery.data) {
      setAiFinance(aiFinanceQuery.data)
    }
  }, [aiFinanceQuery.data])

  const ruleMut = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Record<string, unknown> }) =>
      updateAlertRule(id, payload),
    onSuccess: async () => {
      setError(null)
      await qc.invalidateQueries({ queryKey: ['config', 'alert-rules'] })
    },
    onError: (err: unknown) => {
      setError(err instanceof ApiError ? err.message : 'Failed to update rule')
    },
  })

  const settingsMut = useMutation({
    mutationFn: (settings: Record<string, unknown>) => patchPublicSettings(settings),
    onSuccess: async () => {
      setError(null)
      await qc.invalidateQueries({ queryKey: ['config', 'settings'] })
    },
    onError: (err: unknown) => {
      setError(err instanceof ApiError ? err.message : 'Failed to save threshold')
    },
  })

  const aiFinanceMut = useMutation({
    mutationFn: patchAiFinanceSettings,
    onSuccess: async () => {
      setError(null)
      await qc.invalidateQueries({ queryKey: ['ai', 'settings', 'finance'] })
    },
    onError: (err: unknown) => {
      setError(err instanceof ApiError ? err.message : 'Failed to save AI finance settings')
    },
  })

  const rules = rulesQuery.data ?? []

  return (
    <AdminSection
      title="Alert rules"
      tone="coral"
      icon={Bell}
      lede="Tune weekly prompts, start-date alerts, and the rationale threshold for forecast changes."
    >
      {error && (
        <p className="alert alert--error" role="alert">
          {error}
        </p>
      )}

      <div className="admin-rule-list">
        {rules.map((rule) => (
          <AlertRuleEditor
            key={rule.id}
            rule={rule}
            busy={ruleMut.isPending}
            onSave={(payload) => ruleMut.mutate({ id: rule.id, payload })}
          />
        ))}
        {rules.length === 0 && !rulesQuery.isLoading && (
          <p className="empty">No alert rules configured.</p>
        )}
      </div>

      <form
        className="admin-form"
        onSubmit={(event) => {
          event.preventDefault()
          const pct = Number(rationalePct)
          if (!Number.isFinite(pct) || pct < 0) {
            setError('Enter a valid rationale threshold percentage')
            return
          }
          settingsMut.mutate({ rationale_threshold_pct: pct / 100 })
        }}
      >
        <h3 className="admin-form__title">Rationale threshold</h3>
        <p className="admin-section__lede">
          Percent of forecast-year revenue above which a change rationale is required (seed 10%).
        </p>
        <div className="admin-form__row">
          <label className="field">
            <span className="field__label">Threshold (%)</span>
            <input
              className="field__input"
              type="number"
              min={0}
              step={0.1}
              value={rationalePct}
              onChange={(e) => setRationalePct(e.target.value)}
              required
            />
          </label>
          <button className="button button--primary" type="submit" disabled={settingsMut.isPending}>
            Save threshold
          </button>
        </div>
      </form>

      {aiFinance && (
        <form
          className="admin-form"
          onSubmit={(event) => {
            event.preventDefault()
            aiFinanceMut.mutate({
              soft_limit: aiFinance.soft_limit,
              hard_cap: aiFinance.hard_cap,
              stalled_threshold_days: aiFinance.stalled_threshold_days,
              contract_end_lead_days: aiFinance.contract_end_lead_days,
              still_active_suppression_days: aiFinance.still_active_suppression_days,
              still_active_escalate_after: aiFinance.still_active_escalate_after,
              stall_owner_change_grace_days: aiFinance.stall_owner_change_grace_days,
            })
          }}
        >
          <h3 className="admin-form__title">AI &amp; alert business settings</h3>
          <p className="admin-section__lede">
            Batch caps, stall / contract-end thresholds, and Still-active behaviour (Finance-owned).
          </p>
          <div className="admin-form__row">
            <label className="field">
              <span className="field__label">Soft limit (opps / turn)</span>
              <input
                className="field__input"
                type="number"
                min={1}
                value={aiFinance.soft_limit}
                onChange={(e) => setAiFinance({ ...aiFinance, soft_limit: Number(e.target.value) })}
                required
              />
            </label>
            <label className="field">
              <span className="field__label">Hard cap (opps / turn)</span>
              <input
                className="field__input"
                type="number"
                min={1}
                value={aiFinance.hard_cap}
                onChange={(e) => setAiFinance({ ...aiFinance, hard_cap: Number(e.target.value) })}
                required
              />
            </label>
            <label className="field">
              <span className="field__label">Stalled threshold (days)</span>
              <input
                className="field__input"
                type="number"
                min={1}
                value={aiFinance.stalled_threshold_days}
                onChange={(e) =>
                  setAiFinance({
                    ...aiFinance,
                    stalled_threshold_days: Number(e.target.value),
                  })
                }
                required
              />
            </label>
            <label className="field">
              <span className="field__label">Contract-end lead (days)</span>
              <input
                className="field__input"
                type="number"
                min={1}
                value={aiFinance.contract_end_lead_days}
                onChange={(e) =>
                  setAiFinance({
                    ...aiFinance,
                    contract_end_lead_days: Number(e.target.value),
                  })
                }
                required
              />
            </label>
            <button
              className="button button--primary"
              type="submit"
              disabled={aiFinanceMut.isPending}
            >
              Save AI settings
            </button>
          </div>
        </form>
      )}
    </AdminSection>
  )
}

function AlertRuleEditor({
  rule,
  busy,
  onSave,
}: {
  rule: AlertRule
  busy: boolean
  onSave: (payload: Record<string, unknown>) => void
}) {
  const [text, setText] = useState(() => JSON.stringify(rule.payload ?? {}, null, 2))
  const [localError, setLocalError] = useState<string | null>(null)

  useEffect(() => {
    setText(JSON.stringify(rule.payload ?? {}, null, 2))
  }, [rule.payload, rule.id])

  return (
    <article className="admin-rule">
      <header className="admin-rule__header">
        <h3 className="admin-rule__title">{rule.code}</h3>
        <button
          type="button"
          className="button button--ghost"
          disabled={busy}
          onClick={() => {
            try {
              const payload = JSON.parse(text) as Record<string, unknown>
              if (payload === null || typeof payload !== 'object' || Array.isArray(payload)) {
                setLocalError('Payload must be a JSON object')
                return
              }
              setLocalError(null)
              onSave(payload)
            } catch {
              setLocalError('Invalid JSON')
            }
          }}
        >
          Save payload
        </button>
      </header>
      <textarea
        className="admin-rule__json field__input"
        rows={8}
        spellCheck={false}
        value={text}
        onChange={(e) => {
          setText(e.target.value)
          setLocalError(null)
        }}
        aria-label={`${rule.code} payload`}
      />
      {localError && (
        <p className="alert alert--error" role="alert">
          {localError}
        </p>
      )}
    </article>
  )
}
